import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
/* Toaster / helper are untyped JS (allowJs). */
import { toasterMsg } from '../../../../../../Components/Toaster/Toaster';
import { filePreview } from '../../../../../../Components/helper';
import BombiixPreviewModal from '../../../../../../Components/ModalPopup/BombiixPreviewModal';
import ConfirmationModal from '../../../../../../Components/ModalPopup/ConfirmationModal';
import BombiixAGGrid from '../../../../../../Components/BombiixAGGrid';
import type { BombiixFillBatchItem, BombiixRowId, OnRowChange } from '../../../../../../Components/BombiixAGGrid/types';
import { useMeasurementsDispatch, useMeasurementsState } from './MeasurementsProvider';
import { getSupportingDocs, deleteDocument } from './measurements.service';
import DocumentUploadModal from './DocumentUploadModal';
import { buildColumnDefs, type DocActionHandlers } from './columnDefs';
import { buildGridRows, ROW_ID_SEP, type MeasurementGridRow } from './grid-types';
import { getVisibleSizeNames } from './size-ranges';
import { unstable_batchedUpdates as batchUpdates } from 'react-dom';
import { buildMeasurementFillWrite, SIZE_FIELD_PREFIX } from './measurement-fill';

/** Extensions the shared preview modal cannot render (matches Documents/Document.js). */
const NON_PREVIEWABLE_EXTENSIONS = ['dxf', 'rul', 'wds'];
const fileExtensionOf = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts[parts.length - 1] ?? '').toLowerCase() : '';
};
/** Build a usable S3 URL from a stored document path (http passthrough, else bucket URL). */
const resolveDocUrl = (path: string): string => (path?.startsWith('http') ? path : filePreview(path));

/**
 * Grid viewport height. 153px of chrome sits above it (48px app navbar + 48px Bulk Edit header +
 * 24px module padding + 33px Measurement Unit row) and 89px below it (24px clearance + the 40px
 * floating actions pill + its 25px `bottom` offset), so the horizontal scroll track always ends
 * 24px above the floating bar instead of running underneath it.
 */
const GRID_HEIGHT = 'calc(100vh - 242px)';

const ROW_HEIGHT = 40;
/** Tall enough to show both stacked inputs (measurment + grading) without the border clipping them. */
const ROW_HEIGHT_WITH_GRADING = 76;

/** Split a grid row id (`devOrderId::rowKey`) back into its parts. */
function splitRowId(rowId: BombiixRowId): { devOrderId: string; rowKey: string } {
  const id = String(rowId);
  const separatorAt = id.indexOf(ROW_ID_SEP);
  return { devOrderId: id.slice(0, separatorAt), rowKey: id.slice(separatorAt + ROW_ID_SEP.length) };
}

interface MeasurementsGridProps {
  /** Fetch the next Load-More page of products (BM-9210 pagination). */
  onLoadMore: () => void;
}

/** The multi-product spec grid (BombiixAGGrid) — editable size + tolerance cells, show-grading stacked inputs. */
const MeasurementsGrid: React.FC<MeasurementsGridProps> = ({ onLoadMore }) => {
  const { t } = useTranslation('bulk-edit');
  const state = useMeasurementsState();
  const dispatch = useMeasurementsDispatch();
  const hasMore = state.products.length < state.eligibleProductIds.length;
  const invalidCells = useMemo(() => new Set(state.invalidCells), [state.invalidCells]);

  const rowData = useMemo(() => buildGridRows(state), [state]);
  const [uploadDevOrderId, setUploadDevOrderId] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<MeasurementGridRow | null>(null);
  const [deleteDocRow, setDeleteDocRow] = useState<MeasurementGridRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refreshDocs = useCallback(async () => {
    if (!state.gradingGroupId || state.products.length === 0) return;
    try {
      /* Refresh docs for the currently-loaded products only (no paging) — unloaded pages aren't displayed. */
      const loadedIds = state.products.map((product) => product.devOrderId);
      const supportingDocs = await getSupportingDocs(loadedIds, state.gradingGroupId);
      dispatch({ type: 'DOCS_REFRESHED', payload: { supportingDocs } });
    } catch (error) {
      toasterMsg(error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.DOCS_REFRESH_FAILED'), 'danger');
    }
  }, [state.gradingGroupId, state.products, dispatch, t]);

  const handleDownload = useCallback(
    (row: MeasurementGridRow) => {
      if (!row.document) {
        toasterMsg(t('MEASUREMENTS_MODULE.NO_DOCUMENT'), 'danger');
        return;
      }
      fetch(resolveDocUrl(row.document))
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', row.fileName || 'document');
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => toasterMsg(t('MEASUREMENTS_MODULE.DOWNLOAD_FAILED'), 'danger'));
    },
    [t],
  );

  const handlePreview = useCallback(
    (row: MeasurementGridRow) => {
      if (!row.document) {
        toasterMsg(t('MEASUREMENTS_MODULE.NO_DOCUMENT'), 'danger');
        return;
      }
      if (NON_PREVIEWABLE_EXTENSIONS.includes(fileExtensionOf(row.fileName))) {
        toasterMsg(t('MEASUREMENTS_MODULE.PREVIEW_UNAVAILABLE'), 'danger');
        return;
      }
      setPreviewDoc(row);
    },
    [t],
  );

  const confirmDeleteDoc = useCallback(async () => {
    if (!deleteDocRow) return;
    setDeleting(true);
    try {
      await deleteDocument(deleteDocRow.documentId, deleteDocRow.s3Key);
      toasterMsg(t('MEASUREMENTS_MODULE.DOCUMENT_DELETED'), 'success');
      await refreshDocs();
      setDeleteDocRow(null);
    } catch (error) {
      toasterMsg(error instanceof Error ? error.message : t('MEASUREMENTS_MODULE.DELETE_FAILED'), 'danger');
    } finally {
      setDeleting(false);
    }
  }, [deleteDocRow, refreshDocs, t]);

  const docActions: DocActionHandlers = useMemo(
    () => ({
      onUpload: (row: MeasurementGridRow) => setUploadDevOrderId(row.devOrderId),
      onPreview: handlePreview,
      onDownload: handleDownload,
      onDelete: (row: MeasurementGridRow) => setDeleteDocRow(row),
    }),
    [handlePreview, handleDownload],
  );

  /* Columns show the sizes still visible somewhere in the cohort; the calculation keeps running over the
     full set (`getParentSizeRanges`), which is why a hidden size may vanish from view without changing a
     single measurement (BM-10013). */
  const visibleSizeRanges = useMemo(() => getVisibleSizeNames(state), [state.sizeRanges, state.products]);
  const columnDefs = useMemo(() => buildColumnDefs(visibleSizeRanges, t, docActions, invalidCells), [visibleSizeRanges, t, docActions, invalidCells]);

  const handleRowChange = useCallback<OnRowChange>(
    (rowId, field, value) => {
      const { devOrderId, rowKey } = splitRowId(rowId);
      const nextValue = value === null || value === undefined ? '' : String(value);
      if (field === '__collapse') {
        dispatch({ type: 'TOGGLE_COLLAPSE', payload: { devOrderId } });
        return;
      }
      if (field === 'selected') {
        dispatch({ type: 'TOGGLE_SELECT', payload: { rowId: String(rowId) } });
        return;
      }
      if (field === 'code' || field === 'description') {
        dispatch({ type: 'SET_CODE_DESC', payload: { devOrderId, rowId: rowKey, field, value: nextValue } });
        dispatch({ type: 'CHECK_DIFF', payload: { devOrderId, rowId: rowKey } });
        return;
      }
      if (field === 'tolPlus' || field === 'tolMinus') {
        dispatch({ type: 'SET_TOLERANCE', payload: { devOrderId, rowId: rowKey, field, value: nextValue } });
        return;
      }
      const separatorAt = field.indexOf(':');
      if (separatorAt === -1) return;
      const cellField = field.slice(0, separatorAt);
      const sizeName = field.slice(separatorAt + 1);
      if (cellField === 'measurment' || cellField === 'grading') {
        dispatch({ type: 'COMMIT_CELL', payload: { devOrderId, rowId: rowKey, sizeName, field: cellField, value: nextValue } });
      }
    },
    [dispatch],
  );

  const gridApiRef = useRef<GridApi<MeasurementGridRow> | null>(null);
  const gridOptions = useMemo<GridOptions<MeasurementGridRow>>(
    () => ({
      /* Two-tier header (Figma Multi Spec Table): 40px group row + 40px column row. */
      headerHeight: 40,
      groupHeaderHeight: 40,
      getRowHeight: (params) => (params.data?.activeGrading ? ROW_HEIGHT_WITH_GRADING : ROW_HEIGHT),
      onGridReady: (params: GridReadyEvent<MeasurementGridRow>) => {
        gridApiRef.current = params.api;
      },
    }),
    [],
  );

  /* Show-grading adds a stacked input per size cell; force AG-Grid to re-measure cached row heights. */
  useEffect(() => {
    gridApiRef.current?.resetRowHeights?.();
  }, [state.activeGrading]);

  /* On a failed required-field save, scroll the grid to the first invalid cell, then clear the target. */
  useEffect(() => {
    const target = state.scrollToInvalid;
    const api = gridApiRef.current;
    if (!target || !api) return;
    const node = api.getRowNode(target.rowId);
    if (node?.rowIndex != null) {
      api.ensureIndexVisible(node.rowIndex, 'middle');
      api.ensureColumnVisible(target.colId);
      api.flashCells?.({ rowNodes: [node], columns: [target.colId] });
    }
    dispatch({ type: 'CLEAR_SCROLL_TARGET' });
  }, [state.scrollToInvalid, dispatch]);

  /**
   * Commits a fill-handle drag or paste through the same row-change path a typed value takes, one
   * dispatch per accepted cell inside a single React batch (this tree mounts through the legacy
   * ReactDOM.render, which does not batch state updates outside event handlers). The value AG Grid
   * wrote onto a size column's scratch key is removed again, and refused cells are repainted so
   * they show their stored value.
   */
  const handleFillCells = useCallback(
    (batch: BombiixFillBatchItem<MeasurementGridRow>[], sourceRows: MeasurementGridRow[]) => {
      const refusedRowIds: string[] = [];
      batchUpdates(() => {
        batch.forEach((item) => {
          const write = buildMeasurementFillWrite(item, sourceRows);
          if (item.field.startsWith(SIZE_FIELD_PREFIX)) delete item.data[item.field];
          if (write) handleRowChange(item.rowId, write.field, write.value);
          else refusedRowIds.push(item.data.id);
        });
      });
      const api = gridApiRef.current;
      if (!api || refusedRowIds.length === 0) return;
      const rowNodes = refusedRowIds.map((rowId) => api.getRowNode(rowId)).filter((node): node is NonNullable<typeof node> => Boolean(node));
      if (rowNodes.length) api.refreshCells({ force: true, rowNodes });
    },
    [handleRowChange],
  );

  return (
    <>
      <div className="bulk-measure-unit-title">
        {t('MEASUREMENTS_MODULE.MEASUREMENT_UNIT')}:&nbsp;<span className="bulk-measure-unit-value">{state.conversionType || '—'}</span>
      </div>
      <div className="bulk-measure-grid">
        <BombiixAGGrid<MeasurementGridRow>
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={(row) => row.id}
          onRowChange={handleRowChange}
          enableFillHandle
          onFillCells={handleFillCells}
          height={GRID_HEIGHT}
          gridOptions={gridOptions}
          suppressColumnResize={false}
          fullWidthHorizontalScroll
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          loadingMore={state.loadingMore}
        />
      </div>
      {uploadDevOrderId && state.gradingGroupId && (
        <DocumentUploadModal
          devOrderId={uploadDevOrderId}
          gradingGroupId={state.gradingGroupId}
          onClose={() => setUploadDevOrderId(null)}
          onUploaded={refreshDocs}
        />
      )}
      {previewDoc && (
        <BombiixPreviewModal
          onOpen
          moduleName="SpecDocument"
          data={{ document: previewDoc.document, s3Key: previewDoc.s3Key, mimetype: previewDoc.mimetype, fileName: previewDoc.fileName }}
          toggle={() => setPreviewDoc(null)}
        />
      )}
      {deleteDocRow && (
        <ConfirmationModal
          show
          title={t('MEASUREMENTS_MODULE.DELETE')}
          message={t('MEASUREMENTS_MODULE.DELETE_DOCUMENT_CONFIRM')}
          loading={deleting}
          onConfirm={confirmDeleteDoc}
          onClose={() => setDeleteDocRow(null)}
        />
      )}
    </>
  );
};

export default MeasurementsGrid;
